import { useEffect, useState } from 'react';
import { bridge, isNil, isNotNil } from '../utils';

/**
 * 'stackable-coupon' feature flag.
 *
 * native가 반환하는 페이로드는 promotion 기간 정보를 포함할 수 있다:
 *   { promotion_start_date, promotion_end_date }
 * - 두 필드가 모두 null이면 기간 제한 없이 활성
 * - start만 있으면 start 이후에 활성
 * - end만 있으면 end 이전에 활성
 * - 둘 다 있으면 그 범위 안에서만 활성
 *
 * 페이로드 형식이 아니라 단순 truthy 값이면(legacy/대조군) 그대로 활성으로 간주.
 */
type PromotionPayload = {
  promotion_start_date?: string | null;
  promotion_end_date?: string | null;
};

const KEY = 'stackable-coupon';

export const useStackableCouponFlag = () => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    bridge.getFeatureFlag({ key: KEY }).then(result => {
      setEnabled(isFlagActive(result));
    });
  }, []);

  return enabled;
};

/**
 * 명령형(async) 컨텍스트에서 현재 시점의 stackable coupon 활성 여부를 평가.
 * `useStackableCouponFlag`와 동일한 정책을 공유하기 위한 헬퍼.
 */
export const getStackableCouponEnabled = async (): Promise<boolean> => {
  const result = await bridge.getFeatureFlag({ key: KEY });
  return isFlagActive(result);
};

const isFlagActive = (result: unknown): boolean => {
  if (isNil(result)) return false;
  const payload = parsePromotionPayload(result);
  if (isNil(payload)) return true;
  return isWithinPromotionWindow(payload);
};

const isWithinPromotionWindow = (payload: PromotionPayload): boolean => {
  const now = Date.now();
  if (
    isNil(payload.promotion_start_date) &&
    isNil(payload.promotion_end_date)
  ) {
    return false;
  }
  if (isNotNil(payload.promotion_start_date)) {
    const start = Date.parse(payload.promotion_start_date);
    if (!Number.isNaN(start) && now < start) return false;
  }
  if (isNotNil(payload.promotion_end_date)) {
    const end = Date.parse(payload.promotion_end_date);
    if (!Number.isNaN(end) && now > end) return false;
  }
  return true;
};

const parsePromotionPayload = (result: unknown): PromotionPayload | null => {
  if (typeof result === 'object' && result !== null) {
    return result as PromotionPayload;
  }
  if (typeof result === 'string') {
    try {
      const parsed: unknown = JSON.parse(result);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as PromotionPayload;
      }
    } catch {
      // JSON parse 실패 → 페이로드 아님
    }
  }
  return null;
};
